/**
 * Contact: write to the people who run DevPulse. Signed in or not — the sign-in page links here
 * too. The message reaches the admin area's inbox.
 */

import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Send } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { getSession } from '../lib/auth'
import { isDesktop } from '../lib/desktop'
import { ErrorBanner } from '../components/primitives'

export default function Contact({ standalone = false }: { standalone?: boolean }) {
  const { t } = useI18n()
  const session = getSession()
  const [name, setName] = useState(session?.displayName ?? '')
  const [email, setEmail] = useState(session?.email ?? '')
  const [message, setMessage] = useState('')
  // A field people never see: bots fill it, and the server drops what they send.
  const [website, setWebsite] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  const send = useMutation({
    mutationFn: () => api.contact.send({ name: name.trim(), email: email.trim(), message: message.trim(), source: isDesktop() ? 'desktop' : 'web', website }),
    onError: (error) =>
      setProblem(error instanceof ApiError && error.code && ['invalid_name', 'invalid_email', 'invalid_message'].includes(error.code) ? t(`contact.error.${error.code}`) : t('error.generic')),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setProblem(null)
    send.mutate()
  }

  const body = send.isSuccess ? (
    <div className="stack">
      <p className="small" style={{ margin: 0 }}>{t('contact.thanks')}</p>
      {standalone && <Link to="/sign-in">{t('handoff.signIn')}</Link>}
    </div>
  ) : (
    <form className="stack" onSubmit={submit}>
      <p className="small muted" style={{ margin: 0 }}>{t('contact.intro')}</p>
      <label className="field">
        <span className="field__label">{t('contact.name')}</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required />
      </label>
      <label className="field">
        <span className="field__label">{t('contact.email')}</span>
        <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={320} required />
      </label>
      <label className="field">
        <span className="field__label">{t('contact.message')}</span>
        <textarea className="input" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={5000} required />
      </label>
      <input className="contact__trap" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} aria-hidden="true" name="website" />
      {problem && <ErrorBanner message={problem} />}
      <div className="row row--end">
        <button type="submit" className="btn btn--primary" disabled={send.isPending}>
          <Send size={14} /> {t('contact.send')}
        </button>
      </div>
    </form>
  )

  if (!standalone) return <div className="panel contact" style={{ maxWidth: 640 }}>{body}</div>
  return (
    <div className="auth">
      <div className="auth__card stack">
        <h1 className="small" style={{ margin: 0 }}>{t('contact.title')}</h1>
        {body}
      </div>
    </div>
  )
}
